import { resolveXpointerRange } from "lib/text/xpointer-range";

import type { HighlightTarget } from "./use-highlights";

type QuoteHitTarget = { el: HTMLElement; x: number; y: number };

const HIGHLIGHT_PREFIX = "quote-";

let rangesByQuoteId = new Map<string, Range>();
let registeredColors = new Set<string>();

export function renderHighlights(targets: HighlightTarget[]): {
  clear(): void;
} {
  const colorGroups = new Map<string, Range[]>();

  for (const target of targets) {
    if (rangesByQuoteId.has(target.id)) continue;

    const range = resolveXpointerRange(
      target.startXpointer,
      target.endXpointer
    );
    if (!range) continue;

    rangesByQuoteId.set(target.id, range);

    const group = colorGroups.get(target.color) ?? [];
    group.push(range);
    colorGroups.set(target.color, group);
  }

  for (const [color, ranges] of colorGroups) {
    const name = `${HIGHLIGHT_PREFIX}${color}`;
    const existing = CSS.highlights.get(name);

    if (existing) {
      for (const range of ranges) existing.add(range);
    } else {
      CSS.highlights.set(name, new Highlight(...ranges));
    }

    registeredColors.add(color);
  }

  return {
    clear() {
      for (const color of registeredColors) {
        CSS.highlights.delete(`${HIGHLIGHT_PREFIX}${color}`);
      }
      rangesByQuoteId = new Map();
      registeredColors = new Set();
    },
  };
}

export function getQuoteIdAt({ x, y }: QuoteHitTarget): string | null {
  const caret = document.caretRangeFromPoint(x, y);
  if (!caret) return null;

  for (const [quoteId, range] of rangesByQuoteId) {
    if (range.isPointInRange(caret.startContainer, caret.startOffset)) {
      return quoteId;
    }
  }
  return null;
}

export function getQuoteElement(quoteId: string): Element | null {
  const range = rangesByQuoteId.get(quoteId);
  if (!range) return null;

  const node = range.startContainer;
  const el =
    node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as Element);
  return el;
}

let previewRange: Range | null = null;

export function setPreviewColor(range: Range, color: string) {
  clearPreview();
  previewRange = range;

  const name = `${HIGHLIGHT_PREFIX}${color}`;
  const existing = CSS.highlights.get(name);
  if (existing) {
    existing.add(range);
  } else {
    CSS.highlights.set(name, new Highlight(range));
  }
  registeredColors.add(color);
}

export function clearPreview() {
  if (!previewRange) return;

  for (const [name, highlight] of CSS.highlights) {
    if (name.startsWith(HIGHLIGHT_PREFIX) && highlight.has(previewRange)) {
      highlight.delete(previewRange);
      if (highlight.size === 0) CSS.highlights.delete(name);
      break;
    }
  }
  previewRange = null;
}

export function setQuoteColor(quoteId: string, color: string) {
  const range = rangesByQuoteId.get(quoteId);
  if (!range) {
    console.warn("[setQuoteColor] no range for", quoteId);
    return;
  }

  for (const [name, highlight] of CSS.highlights) {
    if (name.startsWith(HIGHLIGHT_PREFIX) && highlight.has(range)) {
      highlight.delete(range);
      if (highlight.size === 0) CSS.highlights.delete(name);
      break;
    }
  }

  const newName = `${HIGHLIGHT_PREFIX}${color}`;
  const existing = CSS.highlights.get(newName);
  if (existing) {
    existing.add(range);
  } else {
    CSS.highlights.set(newName, new Highlight(range));
  }

  registeredColors.add(color);
}
